'use client';

import React from 'react';
import { Globe } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import type { RegionHealth } from '@/hooks/useDashboardRealtime';

interface Props {
  regions: RegionHealth[];
  loading: boolean;
}

const scoreColor = (s: number) =>
  s >= 90 ? 'text-success' : s >= 70 ? 'text-warning' : 'text-danger';

const statusFromScore = (s: number): 'healthy' | 'degraded' | 'critical' =>
  s >= 90 ? 'healthy' : s >= 70 ? 'degraded' : 'critical';

export default function RegionHealthTable({ regions, loading }: Props) {
  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Globe size={15} className="text-primary" />
        <p className="text-sm font-semibold text-foreground">Region Health Breakdown</p>
        <span className="text-[10px] text-muted font-mono">Addis Ababa Sub-Cities</span>
        {!loading && regions.length > 0 && (
          <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
        )}
      </div>

      <div className="overflow-x-auto">
        {loading && regions.length === 0 ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-9 bg-surface-raised rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Region', 'Status', 'Nodes', 'Incidents', 'Throughput', 'Latency', 'Score'].map((h) => (
                  <th key={`th-region-${h}`} className="text-left pb-2 pr-3 text-[10px] font-semibold uppercase tracking-widest text-muted whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {regions.map((r) => (
                <tr key={r.id} className="border-b border-border/40 hover:bg-surface-raised/50 transition-colors">
                  <td className="py-2.5 pr-3 font-medium text-foreground whitespace-nowrap">{r.name}</td>
                  <td className="py-2.5 pr-3">
                    <StatusBadge status={statusFromScore(r.score)} />
                  </td>
                  <td className="py-2.5 pr-3 font-mono text-[11px]">
                    <span className="text-foreground">{r.healthy_nodes}</span>
                    <span className="text-muted">/{r.total_nodes}</span>
                  </td>
                  <td className="py-2.5 pr-3 font-mono text-[11px]">
                    <span className={r.incidents > 3 ? 'text-danger font-semibold' : r.incidents > 0 ? 'text-warning' : 'text-success'}>
                      {r.incidents}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground-muted">{r.throughput}</td>
                  <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground-muted">{r.latency}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`font-mono font-bold text-sm ${scoreColor(r.score)}`}>{r.score}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}